import React from 'react';
import { Col, Row } from 'antd';
import { I18n } from '@aws-amplify/core';
import { useTheme } from 'styled-components';

import { RISK_TONE, toEnsTone, toScoreTone } from '../utils';
import type { RiskTone } from '../utils';
import { BlockTitle } from '../styles';
import { Badge, ScoreLabel, ScoreValue } from './styles';

const BADGE_GUTTER = 32;
const ROW_GUTTER = 24;
const TIME_UNIT_SEPARATOR = ' ';
const EMPTY_VALUE = '-';

interface ResultsCardProps {
	totalTime: string;
	overallScore: number;
	isEns: boolean;
}

interface TonePalette {
	color: string;
	background: string;
}

function toTotalTime(totalTime: string): string {
	if (!totalTime) {
		return EMPTY_VALUE;
	}

	const [value, unit] = totalTime.split(TIME_UNIT_SEPARATOR);

	if (!unit) {
		return value;
	}

	const result = `${value} ${I18n.get(unit)}`;

	return result;
}

export function ResultsCard({ totalTime, overallScore, isEns }: Readonly<ResultsCardProps>): JSX.Element {
	const theme = useTheme();

	const palettes: Record<RiskTone, TonePalette> = {
		[RISK_TONE.LOW]: { color: theme.palette.risk.low.default, background: theme.palette.risk.low[100] },
		[RISK_TONE.MEDIUM]: { color: theme.palette.risk.medium.default, background: theme.palette.risk.medium[100] },
		[RISK_TONE.HIGH]: { color: theme.palette.risk.high.default, background: theme.palette.risk.high[100] }
	};

	const scorePalette = palettes[toScoreTone(overallScore)];
	const ensPalette = palettes[toEnsTone(isEns)];

	let ensLabel = I18n.get('No');

	if (isEns) {
		ensLabel = I18n.get('Yes');
	}

	return (
		<Row gutter={[0, ROW_GUTTER]}>
			<Col span={24}>
				<BlockTitle>{I18n.get('Results')}</BlockTitle>
			</Col>
			<Col span={24}>
				<ScoreLabel>{I18n.get('Total evaluated time')}</ScoreLabel>
				<ScoreValue>{toTotalTime(totalTime)}</ScoreValue>
			</Col>
			<Col span={24}>
				<Row gutter={[BADGE_GUTTER, 0]}>
					<Col>
						<ScoreLabel>{I18n.get('Total B.E.R.A.')}</ScoreLabel>
						<Badge $color={scorePalette.color} $background={scorePalette.background}>
							{Math.round(overallScore)}
						</Badge>
					</Col>
					<Col>
						<ScoreLabel>{I18n.get('ENS')}</ScoreLabel>
						<Badge $color={ensPalette.color} $background={ensPalette.background}>
							{ensLabel}
						</Badge>
					</Col>
				</Row>
			</Col>
		</Row>
	);
}
